import type { TFunction } from 'i18next';
import type { TenantBqSyncRun } from '@nexus/contracts';

const PARTITION_EMPTY_ERROR = 'PARTITION_EMPTY';
const DOWNSTREAM_FAILED_ERROR = 'DOWNSTREAM_FAILED';

type RunDeltaField = keyof Pick<
  TenantBqSyncRun,
  'productsAdded' | 'productsRemoved' | 'productTypesAdded' | 'productTypesRemoved' | 'industriesAdded' | 'industriesRemoved'
>;

const RUN_DELTAS: Array<{ field: RunDeltaField; key: string; one: string; other: string }> = [
  { field: 'productsAdded', key: 'productsAdded', one: '+{{count}} product', other: '+{{count}} products' },
  { field: 'productsRemoved', key: 'productsRemoved', one: '−{{count}} product', other: '−{{count}} products' },
  { field: 'productTypesAdded', key: 'productTypesAdded', one: '+{{count}} product type', other: '+{{count}} product types' },
  { field: 'productTypesRemoved', key: 'productTypesRemoved', one: '−{{count}} product type', other: '−{{count}} product types' },
  { field: 'industriesAdded', key: 'industriesAdded', one: '+{{count}} industry', other: '+{{count}} industries' },
  { field: 'industriesRemoved', key: 'industriesRemoved', one: '−{{count}} industry', other: '−{{count}} industries' },
];

/** One-line outcome for a sync-history row, e.g. "+3 products, −1 industry" or "No changes". */
export function summarizeSyncRun(t: TFunction, run: TenantBqSyncRun): string {
  if (run.status === 'RUNNING') return t('iam.orgDrawer.settings.bqSync.runSummary.running', 'In progress…');
  if (run.status === 'FAILED') {
    return run.errorCode === PARTITION_EMPTY_ERROR
      ? t('iam.orgDrawer.settings.bqSync.runSummary.partitionEmpty', '0 rows for partition')
      : t('iam.orgDrawer.settings.bqSync.runSummary.failed', 'Sync failed');
  }
  const parts = RUN_DELTAS.filter(({ field }) => run[field] > 0).map(({ field, key, one, other }) =>
    t(`iam.orgDrawer.settings.bqSync.runSummary.${key}`, {
      count: run[field],
      defaultValue_one: one,
      defaultValue_other: other,
    }),
  );
  return parts.length > 0
    ? parts.join(', ')
    : t('iam.orgDrawer.settings.bqSync.runSummary.noChanges', 'No changes');
}

/** Most recent run that finished cleanly — what "Synced …" and the header badge report. */
export function latestSuccessfulRun(runs: TenantBqSyncRun[]): TenantBqSyncRun | null {
  return runs.find((run) => run.status === 'SYNCED' && run.finishedAt) ?? null;
}

export function formatSyncDurationMs(startedAt: string, finishedAt: string | null): string | null {
  if (!finishedAt) return null;
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0 || Number.isNaN(ms)) return null;
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function isLatestRunFailure(latest: TenantBqSyncRun | null | undefined): boolean {
  return latest?.status === 'FAILED';
}

/** A `PARTIAL` run finished but left part of the sync undone. */
export function isLatestRunIncomplete(latest: TenantBqSyncRun | null | undefined): boolean {
  return latest?.status === 'PARTIAL';
}

/**
 * Only a downstream-stage failure refreshes the catalog on a `PARTIAL` run; a skipped
 * catalog pipeline leaves products as they were.
 */
export function isCatalogLeftUnchanged(run: TenantBqSyncRun): boolean {
  return run.status === 'FAILED' || (run.status === 'PARTIAL' && run.errorCode !== DOWNSTREAM_FAILED_ERROR);
}
